import { Button, Card, Combobox, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { memo } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import { DEFAULT_TRANSLATIONS, TRANSLATION_KEY_OPTIONS } from '../constants/translations';
import { useStorefrontOptionsControls } from '../context/storefront-options-context';
import type { LocaleEntry, LocaleGroup } from '../types/spark-options';
import { entryKeyOptions, localeOptions, nextId, updateEntry } from '../utils/locale-rows';
import { duplicateEntryIds, duplicateLocaleIds } from '../utils/locale-validation';
import { entryKeyError, localeError } from './locale-field-errors';
import { withTooltip } from './with-tooltip';

const EDITOR = 'features:storefront_options.components.translations_editor' satisfies I18nPrefix;
const LOCALE = 'features:storefront_options.components.locale_editor' satisfies I18nPrefix;
const TRANSLATION_KEY_FILTER_DEBOUNCE_MS = 300;

export const TranslationsEditor = memo(function TranslationsEditor({
  value,
  onChange,
}: {
  value: LocaleGroup[];
  onChange: (value: LocaleGroup[]) => void;
}) {
  const { showValidationErrors: showErrors } = useStorefrontOptionsControls();
  const duplicates = duplicateLocaleIds(value);
  const canAddLocale = !value.some((group) => group.locale.trim() === '');

  function updateGroup(id: number, next: LocaleGroup) {
    onChange(value.map((group) => (group.id === id ? next : group)));
  }

  function updateEntries(group: LocaleGroup, entries: LocaleEntry[]) {
    updateGroup(group.id, { ...group, entries });
  }

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 && (
        <Text size="sm" variant="secondary">
          {t(`${EDITOR}.empty`)}
        </Text>
      )}

      {value.map((group) => {
        const rows = group.entries;
        const duplicateKeys = duplicateEntryIds(rows);
        const canAddEntry = !rows.some((row) => row.key.trim() === '');

        return (
          <Card size="sm" key={group.id}>
            <Card.Header
              headingLevel="h3"
              primaryAction={{
                variant: 'dangerBare',
                icon: 'delete',
                ariaLabel: t(`${LOCALE}.remove_language`),
                onClick: () => onChange(value.filter((current) => current.id !== group.id)),
              }}
            >
              {group.locale.trim() || t(`${LOCALE}.new_language`)}
            </Card.Header>

            <Card.Body>
              <div className="w-56 max-w-full">
                <Combobox
                  label={t(`${LOCALE}.locale`)}
                  options={localeOptions(value, group.id)}
                  placeholder={t(`${LOCALE}.locale_placeholder`)}
                  value={group.locale || null}
                  allowCustomValue
                  error={localeError(group.locale, duplicates.has(group.id), showErrors)}
                  onChange={(next) => updateGroup(group.id, { ...group, locale: next ?? '' })}
                />
              </div>

              {rows.length === 0 ? (
                <Text size="sm" variant="secondary">
                  {t(`${EDITOR}.empty_entries`)}
                </Text>
              ) : (
                <div className="grid grid-cols-[1fr_1fr_1.5rem] items-center gap-2">
                  <Text size="sm" variant="secondary">
                    {t(`${EDITOR}.key`)}
                  </Text>
                  <Text size="sm" variant="secondary">
                    {t(`${EDITOR}.value`)}
                  </Text>
                  <span aria-hidden />
                </div>
              )}

              {rows.map((row) => (
                <div className="grid grid-cols-[1fr_1fr_1.5rem] items-center gap-2" key={row.id}>
                  <Combobox
                    options={entryKeyOptions(rows, row.id, TRANSLATION_KEY_OPTIONS)}
                    placeholder={t(`${EDITOR}.key_placeholder`)}
                    noOptionsMessage={t(`${EDITOR}.key_no_options`)}
                    value={row.key || null}
                    allowCustomValue
                    filterDebounceMs={TRANSLATION_KEY_FILTER_DEBOUNCE_MS}
                    error={entryKeyError(row.key, duplicateKeys.has(row.id), showErrors)}
                    onChange={(next) =>
                      updateEntries(group, updateEntry(rows, row.id, { key: next ?? '' }))
                    }
                  />
                  <Input
                    placeholder={
                      DEFAULT_TRANSLATIONS[row.key.trim()] || t(`${EDITOR}.value_placeholder`)
                    }
                    value={row.value}
                    onChange={(next) =>
                      updateEntries(group, updateEntry(rows, row.id, { value: next }))
                    }
                  />
                  <Button
                    variant="dangerBare"
                    icon="delete"
                    ariaLabel={t(`${EDITOR}.remove_translation`)}
                    onClick={() =>
                      updateEntries(
                        group,
                        rows.filter((current) => current.id !== row.id),
                      )
                    }
                  />
                </div>
              ))}

              <div>
                <Button
                  variant="secondary"
                  size="xs"
                  icon="plusCircle"
                  disabled={!canAddEntry}
                  onClick={() =>
                    updateEntries(group, [...rows, { id: nextId(rows), key: '', value: '' }])
                  }
                >
                  {t(`${EDITOR}.add_translation`)}
                </Button>
              </div>
            </Card.Body>
          </Card>
        );
      })}

      <div>
        {withTooltip(
          !canAddLocale && t(`${LOCALE}.add_language_disabled_hint`),
          <Button
            variant="secondary"
            icon="plusCircle"
            disabled={!canAddLocale}
            onClick={() =>
              onChange([
                ...value,
                { id: nextId(value), locale: '', entries: [{ id: 0, key: '', value: '' }] },
              ])
            }
          >
            {t(`${LOCALE}.add_language`)}
          </Button>,
        )}
      </div>
    </div>
  );
});
